import React from "react";
import PropTypes from "prop-types";

import { useAccessTypeStore } from "scenes/Hubly/components/Workspace/components/hooks";

import { FieldDetailsModal, FormLayout, TaskIcon, useBooleanState } from "..";
import { FormFieldContent, FormFieldMenu } from "./components";

export function FormField({
  formName,
  field,
  value,
  prefix,
  hideMenu,
  inlineEditor,
  editMode,
  onEnableEditMode,
  onDelete,
  onAddFormFieldAbove,
  onEditField,
}) {
  const { hasAccess } = useAccessTypeStore();
  const [
    isFieldDetailsModalOpened,
    openFieldDetailsModal,
    closeFieldDetailsModal,
  ] = useBooleanState();

  if (editMode && hasAccess()) {
    return <FormLayout icon="clipboard">{inlineEditor}</FormLayout>;
  }

  return (
    <React.Fragment>
      <FormLayout
        loading={field.loading}
        hideMenu={hideMenu || !hasAccess()}
        disableHover={!hasAccess()}
        icon="clipboard"
        actionIcons={
          <div style={{ floatRight: true }} data-test="edit-field-details">
            <TaskIcon
              iconName="align left"
              iconNameOutline="align left"
              title="Edit Field Details"
              onClick={openFieldDetailsModal}
              hideAddIcon
            />
          </div>
        }
        popupMenu={
          <FormFieldMenu
            onDelete={onDelete}
            onEnableEditMode={onEnableEditMode}
            onAddFormFieldAbove={onAddFormFieldAbove}
          />
        }
      >
        <FormFieldContent
          {...field}
          value={value}
          prefix={prefix}
          hasAccess={hasAccess()}
          onClick={onEnableEditMode}
        />
      </FormLayout>
      {isFieldDetailsModalOpened && (
        <FieldDetailsModal
          loading={field.loading}
          field={field}
          subtitle={`${formName} / ${field.name}`}
          onSave={async (data) => {
            await onEditField({
              ...data,
              name: field.name,
              type: field.type.id,
              ...(Array.isArray(value) && value.length
                ? { options: value }
                : {}),
            });
            closeFieldDetailsModal();
          }}
          onClose={closeFieldDetailsModal}
        />
      )}
    </React.Fragment>
  );
}

FormField.propTypes = {
  field: PropTypes.shape({
    loading: PropTypes.bool,
    name: PropTypes.string,
    type: PropTypes.object,
    required: PropTypes.bool,
    description: PropTypes.string,
  }).isRequired,
  formName: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.instanceOf(Date),
  ]),
  prefix: PropTypes.string,
  hideMenu: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  inlineEditor: PropTypes.node,
  editMode: PropTypes.bool,
  onEnableEditMode: PropTypes.func,
  onAddFormFieldAbove: PropTypes.func,
  onEditField: PropTypes.func,
};

FormField.defaultProps = {
  value: null,
  formName: "Form",
  prefix: "",
  hideMenu: false,
  inlineEditor: null,
  editMode: false,
  onEnableEditMode: () => {},
  onAddFormFieldAbove: () => {},
  onEditField: () => {},
};

export default FormField;
